import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useExtensionDetected } from "../hooks/useExtensionDetected";

const CHROME_STORE_URL =
  "https://chromewebstore.google.com/detail/duneworks-ai/lleekikobhbnleadhcjmfjpiflcpehmc?utm_source=gmail&utm_medium=email&utm_campaign=early_access_launch";

interface ExtensionSidebarButtonProps {
  isOpen: boolean;
}

export function ExtensionSidebarButton({
  isOpen,
}: ExtensionSidebarButtonProps) {
  const handleInstall = () => {
    window.open(CHROME_STORE_URL, "_blank", "noopener,noreferrer");
  };
  const isInstalled = useExtensionDetected();
  const [showPopup, setShowPopup] = useState(false);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const updatePopupPosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const margin = 12;
    const maxH = viewportH - margin * 2;
    // Place popup flush right of sidebar, bottom-aligned to button
    setPopupPos({
      left: rect.right + 2,
      top: margin,
    });
    requestAnimationFrame(() => {
      if (!popupRef.current) return;
      const popupH = popupRef.current.scrollHeight;
      // Ideal: bottom of popup aligns with bottom of button
      let top = rect.bottom - Math.min(popupH, maxH);
      // Clamp top so it doesn't go above viewport
      if (top < margin) top = margin;
      // Clamp bottom so it doesn't go below viewport
      if (top + Math.min(popupH, maxH) > viewportH - margin) {
        top = viewportH - Math.min(popupH, maxH) - margin;
      }
      setPopupPos({ left: rect.right + 2, top });
    });
  }, []);

  const handleMouseEnter = () => {
    updatePopupPosition();
    setShowPopup(true);
  };

  const handleMouseLeave = (e: React.MouseEvent) => {
    const related = e.relatedTarget as Node | null;
    if (
      popupRef.current?.contains(related) ||
      btnRef.current?.contains(related)
    )
      return;
    setShowPopup(false);
  };

  const handlePopupLeave = (e: React.MouseEvent) => {
    const related = e.relatedTarget as Node | null;
    if (btnRef.current?.contains(related)) return;
    setShowPopup(false);
  };

  if (isInstalled) return null;

  return (
    <>
      <div
        ref={btnRef}
        className="relative px-2 py-1"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Button */}
        <button
          onClick={handleInstall}
          className={`relative flex items-center ${
            isOpen ? "gap-3 w-full" : "justify-center w-full"
          } p-3 rounded-lg transition-colors border border-[#E8922A]/30 dark:border-[#E8922A]/20 hover:bg-[#F5E9D7] dark:hover:bg-[#2C405F] overflow-hidden`}
        >
          {/* Pulsing rings — collapsed only */}
          {!isOpen && (
            <>
              <div className="pointer-events-none absolute inset-0 rounded-lg border border-[#E8922A]/40 animate-[ext-ring_2.8s_ease-in-out_infinite]" />
              <div className="pointer-events-none absolute inset-[2px] rounded-lg border border-[#E8922A]/30 animate-[ext-ring_2.8s_ease-in-out_0.55s_infinite]" />
            </>
          )}

          {/* Icon */}
          <div className="relative flex-shrink-0">
            <svg
              viewBox="0 0 20 20"
              fill="none"
              className="w-5 h-5 animate-[icon-pulse_2.8s_ease-in-out_infinite]"
            >
              <rect
                x="1.5"
                y="4.5"
                width="12"
                height="8.5"
                rx="2"
                stroke="#E8922A"
                strokeWidth="1.4"
              />
              <path
                d="M13.5 8h2.5a1 1 0 011 1v2a1 1 0 01-1 1h-2.5"
                stroke="#E8922A"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
              <circle cx="7.5" cy="8.75" r="1.3" fill="#E8922A" />
              <path
                d="M5 13v1.5M7.5 13v1.5M10 13v1.5"
                stroke="#E8922A"
                strokeWidth="1.2"
                strokeLinecap="round"
              />
            </svg>
            {/* Activity dot */}
            <div className="absolute -top-[3px] -right-[3px] w-2 h-2 rounded-full bg-[#FF9B2E] border-[1.5px] border-[#FDFAF7] dark:border-[#122135] animate-[dot-pop_2.6s_ease-in-out_infinite]" />
          </div>

          {isOpen && (
            <span
              className="text-sm font-medium text-[#E8922A] whitespace-nowrap"
              style={{ fontFamily: "Sofia Pro, sans-serif" }}
            >
              Get Extension
            </span>
          )}

          {/* Shine sweep */}
          {!isOpen && (
            <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
              <div className="absolute top-0 -left-full h-full w-3/5 bg-gradient-to-r from-transparent via-[rgba(255,186,85,0.25)] to-transparent animate-[sidebar-shine_2.6s_ease-in-out_infinite]" />
            </div>
          )}
        </button>
      </div>

      {/* Hover popup — rendered via portal to escape overflow:hidden */}
      {showPopup &&
        createPortal(
          <div
            ref={popupRef}
            onMouseLeave={handlePopupLeave}
            className="fixed z-[9999] w-[296px] transition-all duration-200 ease-out overflow-y-auto rounded-[18px]"
            style={{
              left: popupPos.left,
              top: popupPos.top,
              maxHeight: "calc(100vh - 24px)",
            }}
          >
            {" "}
            <div className="rounded-[18px] border border-[#E5DDD3] dark:border-[#2C405F] bg-[#FDFAF7] dark:bg-[#0A1423] overflow-hidden shadow-2xl">
              {/* Hero */}
              <div className="relative overflow-hidden p-[18px_18px_16px] border-b border-[#E5DDD3] dark:border-[#2C405F]">
                <div className="pointer-events-none absolute -bottom-10 -right-10 w-[130px] h-[130px] rounded-full bg-[radial-gradient(circle,rgba(232,146,42,0.06)_0%,transparent_70%)]" />

                <div className="relative z-[1] flex items-center gap-[9px] mb-[14px]">
                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[9px] border border-[#E5DDD3] dark:border-[#2C405F] bg-[#F5EFE6] dark:bg-[#122135]">
                    <svg viewBox="0 0 48 48" fill="none" className="w-5 h-5">
                      <circle cx="24" cy="24" r="22" fill="#1A1208" />
                      <ellipse cx="24" cy="30" rx="14" ry="10" fill="#E8922A" />
                      <circle cx="24" cy="18" r="4" fill="#fff" />
                    </svg>
                  </div>
                  <span className="text-[12px] font-semibold tracking-[0.02em] text-[#6A5F54] dark:text-[#9AAABF]">
                    Duneworks AI
                  </span>
                  <span className="ml-auto text-[9px] font-bold tracking-[0.07em] uppercase text-[#1A1208] bg-[#E8922A] px-[9px] py-[2px] rounded-full">
                    Free
                  </span>
                </div>

                <div
                  className="relative z-[1] text-[17.5px] font-bold text-[#1D1B19] dark:text-[#E3E6EA] leading-[1.28] tracking-[-0.02em] mb-2"
                  style={{ fontFamily: "Sofia Pro, sans-serif" }}
                >
                  Your <em className="not-italic text-[#E8922A]">autonomous</em>
                  <br />
                  AI twin — smarter
                  <br />
                  decisions, less effort.
                </div>
                <div className="relative z-[1] text-[11px] text-[#8E8378] dark:text-[#6E829E] leading-[1.6]">
                  Point it at any problem on your screen. Get context, clarity,
                  and a path forward — instantly.
                </div>
              </div>

              {/* USP rows */}
              <div className="flex flex-col gap-[1px] p-[10px_12px] border-b border-[#E5DDD3] dark:border-[#2C405F]">
                {/* Autonomous mode */}
                <div className="flex items-start gap-[11px] p-[9px] rounded-[10px] transition-colors hover:bg-[#F5EFE6] dark:hover:bg-[#122135]">
                  <div className="mt-[1px] flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5DDD3] dark:border-[#2C405F] bg-[#F5EFE6] dark:bg-[#0A1423]">
                    <svg
                      viewBox="0 0 13 13"
                      fill="none"
                      className="w-[13px] h-[13px]"
                    >
                      <path
                        d="M6.5 1.5C4 1.5 2 3.5 2 6a4.4 4.4 0 003 4.2V11h3v-.8A4.4 4.4 0 0011 6c0-2.5-2-4.5-4.5-4.5z"
                        stroke="#C8922A"
                        strokeWidth="1.1"
                      />
                      <path
                        d="M4.5 6h4M6.5 4v4"
                        stroke="#C8922A"
                        strokeWidth="1"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11.5px] font-semibold text-[#2C405F] dark:text-[#9AAABF] mb-[3px]">
                      Autonomous mode
                    </div>
                    <div className="text-[10.5px] text-[#6A5F54] dark:text-[#6E829E] leading-[1.55]">
                      When you hit the button, it reads what's on your screen,
                      understands the problem in full, and figures out the best
                      path to resolve it — no manual digging required.
                    </div>
                    <div className="mt-[6px] flex flex-wrap gap-[5px]">
                      <span className="inline-flex items-center text-[9.5px] font-medium text-[#C8922A] bg-[#FFF8EE] dark:bg-[#122135] border border-[#E8D5B8] dark:border-[#2C405F] rounded-[5px] px-[7px] py-[2px]">
                        on-demand · you stay in control
                      </span>
                    </div>
                  </div>
                </div>

                {/* Unified stack */}
                <div className="flex items-start gap-[11px] p-[9px] rounded-[10px] transition-colors hover:bg-[#F5EFE6] dark:hover:bg-[#122135]">
                  <div className="mt-[1px] flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5DDD3] dark:border-[#2C405F] bg-[#F5EFE6] dark:bg-[#0A1423]">
                    <svg
                      viewBox="0 0 13 13"
                      fill="none"
                      className="w-[13px] h-[13px]"
                    >
                      <circle
                        cx="3"
                        cy="6.5"
                        r="1.4"
                        stroke="#C8922A"
                        strokeWidth="1.1"
                      />
                      <circle
                        cx="10"
                        cy="3"
                        r="1.4"
                        stroke="#C8922A"
                        strokeWidth="1.1"
                      />
                      <circle
                        cx="10"
                        cy="10"
                        r="1.4"
                        stroke="#C8922A"
                        strokeWidth="1.1"
                      />
                      <path
                        d="M4.4 5.9l4.2-2.4M4.4 7.1l4.2 2.4"
                        stroke="#C8922A"
                        strokeWidth="1"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11.5px] font-semibold text-[#2C405F] dark:text-[#9AAABF] mb-[3px]">
                      Your entire stack, unified
                    </div>
                    <div className="text-[10.5px] text-[#6A5F54] dark:text-[#6E829E] leading-[1.55]">
                      Pulls in signals from your connected tools and surfaces
                      what's relevant to your current problem — right in your
                      dashboard.
                    </div>
                    <div className="mt-[6px] flex flex-wrap gap-[5px]">
                      {["Datadog", "Jira", "AWS", "PagerDuty", "+ more"].map(
                        (tool) => (
                          <span
                            key={tool}
                            className="text-[9.5px] font-medium text-[#6A5F54] dark:text-[#6E829E] bg-[#F5EFE6] dark:bg-[#0A1423] border border-[#E5DDD3] dark:border-[#2C405F] rounded-[5px] px-[7px] py-[2px]"
                          >
                            {tool}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </div>

                {/* Insights */}
                <div className="flex items-start gap-[11px] p-[9px] rounded-[10px] transition-colors hover:bg-[#F5EFE6] dark:hover:bg-[#122135]">
                  <div className="mt-[1px] flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5DDD3] dark:border-[#2C405F] bg-[#F5EFE6] dark:bg-[#0A1423]">
                    <svg
                      viewBox="0 0 13 13"
                      fill="none"
                      className="w-[13px] h-[13px]"
                    >
                      <rect
                        x="1.5"
                        y="1.5"
                        width="10"
                        height="10"
                        rx="2"
                        stroke="#C8922A"
                        strokeWidth="1.1"
                      />
                      <path
                        d="M3 8.5l2-2.5 2 2 2.5-3"
                        stroke="#C8922A"
                        strokeWidth="1.1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11.5px] font-semibold text-[#2C405F] dark:text-[#9AAABF] mb-[3px]">
                      Insights that drive decisions
                    </div>
                    <div className="text-[10.5px] text-[#6A5F54] dark:text-[#6E829E] leading-[1.55]">
                      From root-cause reports to trend summaries and impact
                      analysis — everything you need to understand a situation
                      and act with confidence.
                    </div>
                    <div className="mt-[6px] flex flex-wrap gap-[5px]">
                      {["RCA", "trends", "impact", "recommendations"].map(
                        (tag) => (
                          <span
                            key={tag}
                            className="text-[9.5px] font-medium text-[#6A5F54] dark:text-[#6E829E] bg-[#F5EFE6] dark:bg-[#0A1423] border border-[#E5DDD3] dark:border-[#2C405F] rounded-[5px] px-[7px] py-[2px]"
                          >
                            {tag}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="p-[10px_13px_14px]">
                <button
                  onClick={handleInstall}
                  className="relative w-full flex items-center gap-[9px] overflow-hidden rounded-[10px] bg-[#F0E8D8] dark:bg-[#E3E6EA] px-3 py-[9px] border-none cursor-pointer transition-colors hover:bg-[#E8DEC8] dark:hover:bg-[#D1D5DB]"
                >
                  <div className="pointer-events-none absolute inset-0 animate-[sidebar-shine_2.4s_ease-in-out_0.5s_infinite]">
                    <div className="absolute top-0 -left-full h-full w-3/5 bg-gradient-to-r from-transparent via-[rgba(232,146,42,0.14)] to-transparent" />
                  </div>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className="relative z-[1] w-5 h-5 flex-shrink-0"
                  >
                    <circle cx="12" cy="12" r="4.5" fill="#1A73E8" />
                    <path
                      d="M12 7.5h9.03a10.5 10.5 0 00-9.03-5.25"
                      fill="#EA4335"
                    />
                    <path
                      d="M3.27 17.25L7.8 9.38A10.5 10.5 0 012.97 17.25"
                      fill="#FBBC05"
                    />
                    <path
                      d="M7.8 9.38A10.5 10.5 0 0121.03 7.5H12a4.5 4.5 0 00-4.2 1.88z"
                      fill="#EA4335"
                    />
                    <path
                      d="M12 16.5a4.5 4.5 0 01-4.2-7.12L3.27 17.25a10.5 10.5 0 0017.46.75L16.5 12a4.5 4.5 0 01-4.5 4.5z"
                      fill="#34A853"
                    />
                    <path
                      d="M20.73 18a10.5 10.5 0 00.3-.75L16.5 12a4.5 4.5 0 01-4.5 4.5 4.5 4.5 0 004.23 2.98A10.5 10.5 0 0020.73 18z"
                      fill="#FBBC05"
                    />
                  </svg>
                  <div className="relative z-[1] flex-1 text-left">
                    <div className="text-[9px] font-medium text-[#8E8378]">
                      Add to Chrome
                    </div>
                    <div
                      className="text-[12px] font-bold text-[#1A1208]"
                      style={{ fontFamily: "Sofia Pro, sans-serif" }}
                    >
                      Get the extension — free
                    </div>
                  </div>
                  <div className="relative z-[1] flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#E8922A]">
                    <svg
                      viewBox="0 0 10 10"
                      fill="none"
                      className="w-[9px] h-[9px]"
                    >
                      <path
                        d="M2 8L8 2M8 2H4M8 2v4"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
                <div className="text-center text-[9.5px] text-[#2A2010] dark:text-[#6E829E] mt-[7px]">
                  Works on any tab ·{" "}
                  <span className="text-[#4A3818] dark:text-[#9AAABF]">
                    No credit card needed
                  </span>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
